import { useMemo, useState, useCallback } from 'react'
import styled from '@emotion/styled'
import List from '@mui/material/List'
import { FieldMapperRow, MappingControl, FieldMapperHeader } from './components'
import {
  sortByRequired,
  sortBySchema,
  IntegrationMappableField,
  TemplateMappingOption,
  IntegrationMappingField,
  filterCVFieldsByIntegrationType,
  MappingType,
} from './utils'

const ListNoPadding = styled(List)`
  padding-left: 0px;
`

export interface DependentMappings {
  integrationOptions?: IntegrationMappableField[]
  templateOptions?: TemplateMappingOption[]
  resetFieldMappings: () => void
}

interface Props {
  integrationOptions: IntegrationMappableField[]
  templateOptions: TemplateMappingOption[]
  onSelect: (value: any) => void
  mappings: IntegrationMappingField[]
  integrationTitle: string
  mappingType: MappingType
  onIntegrationChange?: (schema: any, mappings: IntegrationMappingField[]) => void
  integration: any
  mappingsDisabled?: boolean
  dependentMappings?: DependentMappings
}

export const CvsFieldMapperV2 = ({
  integrationOptions,
  templateOptions,
  onSelect,
  mappings,
  integrationTitle,
  mappingType,
  onIntegrationChange,
  integration,
  mappingsDisabled,
  dependentMappings,
}: Props) => {
  const [extraRows, setExtraRows] = useState<number[]>([])

  const sortByIntegrationType = (mappings: IntegrationMappingField[]) => {
    switch (integrationTitle) {
      // currently just Google Analytics uses the sort by schema functionality
      // other integrations will use this as they convert to field mapper v2
      case 'Google Analytics':
        return sortBySchema(integration.metadata?.schema, [...mappings])
      default:
        return [...mappings].sort((a, b) => sortByRequired(a.required, b.required))
    }
  }

  function getEmptyFieldMapping(): IntegrationMappingField {
    return {
      cvFieldId: '',
      fieldSourceType: 'field',
      fieldName: '',
      required: false,
      type: 'integration_field',
    }
  }

  function handleFieldDeletion(index: number) {
    const newMappings = mappings.filter((_, i) => i !== index)

    onSelect(newMappings)
  }

  function handleFieldSelection(field: IntegrationMappingField, index: number) {
    if (index == -1) {
      const newMappings = mappings.slice().concat([field])
      onSelect(newMappings)
      return
    }

    const newMappings = mappings.slice()
    newMappings[index] = field

    onSelect(newMappings)
  }

  const addRow = useCallback(() => {
    setExtraRows(old => (mappings.length >= old.length ? [...old, old.length] : old))
  }, [setExtraRows, mappings])

  const mappedExtraRows = useMemo(
    () =>
      !extraRows.length
        ? null
        : extraRows.map((opt, i) => {
            return (
              <FieldMapperRow
                index={i}
                integrationSlug={integration.slug}
                mappingType={mappingType}
                integrationTitle={integrationTitle}
                key={opt}
                integrationOptions={integrationOptions}
                availableOptions={integrationOptions.filter(
                  i => !mappings.find(m => m.fieldName == i.fieldName),
                )}
                templateOptions={templateOptions.filter(f =>
                  filterCVFieldsByIntegrationType(mappingType, f),
                )}
                availableTemplateOptions={templateOptions.filter(
                  t =>
                    filterCVFieldsByIntegrationType(mappingType, t) &&
                    !mappings.find(v => v.cvFieldId == t.value),
                )}
                value={getEmptyFieldMapping()}
                onDelete={() => {
                  setExtraRows(old => {
                    const copy = [...old]
                    copy.splice(i, 1)
                    return copy
                  })
                }}
                onSelect={(val, _) => {
                  setExtraRows(old => {
                    const copy = [...old]
                    copy.splice(i, 1)
                    return copy
                  })
                  handleFieldSelection(val, -1)
                }}
              />
            )
          }),
    [extraRows, onSelect, mappings],
  )

  const mappedRows = useMemo(
    () =>
      mappings &&
      sortByIntegrationType(mappings).map((mapping, index) => (
        <FieldMapperRow
          shouldShowInputHeader={index === 0}
          integrationSlug={integration?.slug}
          mappingType={mappingType}
          integrationTitle={integrationTitle}
          key={mapping.fieldName}
          integrationOptions={integrationOptions}
          availableOptions={integrationOptions.filter(
            i => !mappings.find(m => m.fieldName == i.fieldName),
          )}
          templateOptions={templateOptions.filter(f =>
            filterCVFieldsByIntegrationType(mappingType, f),
          )}
          availableTemplateOptions={templateOptions.filter(
            t =>
              filterCVFieldsByIntegrationType(mappingType, t) &&
              !mappings.find(v => v.cvFieldId == t.value),
          )}
          index={index}
          value={mapping}
          onDelete={() => {
            handleFieldDeletion(index)
          }}
          onSelect={handleFieldSelection}
          mappingsDisabled={mappingsDisabled}
        />
      )),
    [mappings, onSelect],
  )
  return (
    <ListNoPadding>
      <FieldMapperHeader />
      {mappedRows}
      {mappedExtraRows}
      <MappingControl
        mappings={mappings}
        templateOptions={templateOptions}
        addRow={addRow}
        integrationTitle={integrationTitle}
        integrationOptions={integrationOptions}
        onIntegrationChange={onIntegrationChange}
        integration={integration}
        mappingsDisabled={mappingsDisabled}
        dependentMappings={dependentMappings}
      />
    </ListNoPadding>
  )
}
